import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import { CSSObject } from 'styled-components';

import Button from '@adsk/alloy-react-button';
import { optionalPropType } from '@adsk/alloy-react-helpers';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  RenderIconType,
} from '@adsk/alloy-react-icon';
import theme, { stylePropType } from '@adsk/alloy-react-theme';

import Cell from './Cell';
import YearMonthDropdown from './YearMonthDropdown';
import {
  getCalendarNavigationProps,
  getDateVariant,
  isForwardDisabled,
  isBackwardDisabled,
} from '../helpers';
import useDateSelection from '../hooks/useDateSelection';
import useNavigation from '../hooks/useNavigation';
import useRangeHover from '../hooks/useRangeHover';
import * as types from '../types';

const ARROW_BUTTONS_STYLES: CSSObject = {
  position: 'absolute',
  color: theme.tokens.colors.icon.primary.value,
  '&:disabled': {
    color: theme.tokens.colors.icon.inactive.value,
  },
};

type UseRangeHover = ReturnType<typeof useRangeHover>;

type CalendarProps = Pick<
  types.DatePickerAllArgs,
  | 'startDate'
  | 'endDate'
  | 'minDate'
  | 'maxDate'
  | 'hasEventForDate'
  | 'tooltipContentForDate'
> &
  Pick<
    ReturnType<typeof useNavigation>,
    'setMonth' | 'setYear' | 'prevMonth' | 'nextMonth' | 'updateActiveMonths'
  > &
  Pick<ReturnType<typeof useDateSelection>, 'onDateSelected'> &
  types.CalendarProps & {
    calendars: types.CalendarType[];
    calendar: types.CalendarType;
    onDateHover?: UseRangeHover[1];
    stateHoveredDate?: UseRangeHover[0];
    range?: boolean;
    shouldShowSelection?: boolean;
  };

const renderLeftIcon: RenderIconType = (p) => <ChevronLeftIcon {...p} />;
const renderRightIcon: RenderIconType = (p) => <ChevronRightIcon {...p} />;

const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      className,
      style,
      calendar,
      calendars,
      minDate,
      maxDate,
      onDateHover,
      onDateSelected,
      updateActiveMonths,
      hasEventForDate,
      tooltipContentForDate,
      stateHoveredDate,
      prevMonth,
      nextMonth,
      setMonth,
      setYear,
      startDate,
      endDate,
      range,
      shouldShowSelection,
      ...rest
    },
    ref,
  ) => {
    const {
      selectedMonth,
      selectedYear,
      weekdays,
      weeks,
      months,
      years,
      year,
      month,
      index,
    } = calendar;

    const { hideBack, hideForward } = getCalendarNavigationProps({
      calendarKey: index,
      totalCalendars: calendars.length,
    });

    return (
      <div
        {...rest}
        ref={ref}
        className={className}
        css={[
          {
            display: 'flex',
            flexDirection: 'column',
            width: `calc(100% / ${calendars.length})`,
            padding: `0 ${theme.spacing.M}px`,
          },
          style,
        ]}
      >
        <div
          css={[
            {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: theme.spacing.S,
              height: 40,
              position: 'relative',
            },
          ]}
        >
          {!hideBack && (
            <Button
              renderIcon={renderLeftIcon}
              variant={Button.VARIANTS.TERTIARY}
              style={[
                {
                  ...ARROW_BUTTONS_STYLES,
                  left: 0,
                },
              ]}
              onClick={prevMonth}
              disabled={isBackwardDisabled({ calendars, minDate })}
              data-testid="DatePickerArrowBack"
            />
          )}
          <div css={{ display: 'flex' }}>
            <YearMonthDropdown
              items={months}
              selectedItem={selectedMonth}
              onSelectedItemChange={({ selectedItem }) => {
                if (selectedItem) {
                  setMonth(calendar.index, selectedItem.key);
                }
              }}
              disabled={!months || months.length <= 1}
              data-testid={`DatePickerCalendar-${calendar.index}-month`}
            >
              {selectedMonth?.label}
            </YearMonthDropdown>
            <YearMonthDropdown
              items={years}
              selectedItem={selectedYear}
              style={{ marginLeft: theme.spacing.M }}
              onSelectedItemChange={({ selectedItem }) => {
                if (selectedItem) {
                  setYear(calendar.index, selectedItem.key);
                }
              }}
              disabled={!years || years.length <= 1}
              data-testid={`DatePickerCalendar-${calendar.index}-year`}
            >
              {selectedYear?.label}
            </YearMonthDropdown>
          </div>
          {!hideForward && (
            <Button
              renderIcon={renderRightIcon}
              variant={Button.VARIANTS.TERTIARY}
              style={[
                {
                  ...ARROW_BUTTONS_STYLES,
                  right: 0,
                },
              ]}
              onClick={nextMonth}
              disabled={isForwardDisabled({ calendars, maxDate })}
              data-testid="DatePickerArrowForward"
            />
          )}
        </div>
        <div
          key={`${month}${year}`}
          css={[
            {
              display: 'flex',
              flexWrap: 'wrap',
            },
          ]}
        >
          {useMemo(
            () =>
              weekdays.map((weekday) => (
                <Cell
                  key={`${month}/${year}-${weekday}`}
                  variant={Cell.VARIANTS.SPACER}
                >
                  {weekday}
                </Cell>
              )),
            [weekdays, month, year],
          )}
          {useMemo(
            () =>
              weeks.map((week, weekIndex) =>
                week.map((dateObj, dayIndex) => {
                  const key = `${month}/${year}-${dayIndex}/${weekIndex}`;
                  let selectedDates = [startDate, endDate];

                  if (!dateObj) {
                    return <Cell key={key} variant={Cell.VARIANTS.SPACER} />;
                  }

                  if (startDate && endDate && startDate > endDate) {
                    selectedDates = [endDate, startDate];
                  }

                  const currentVariant: types.CellVariant = getDateVariant({
                    range,
                    selectedDates,
                    date: dateObj.date,
                    stateHoveredDate,
                    isOutside: dateObj.isOutside,
                    shouldShowSelection,
                  });

                  return (
                    <Cell
                      key={key}
                      data-testid={`data-cell-${currentVariant}`}
                      today={dateObj.today}
                      selected={dateObj.selected && shouldShowSelection}
                      disabled={!dateObj.selectable}
                      hasEvent={!!hasEventForDate?.({ ...dateObj })}
                      tooltipContent={
                        tooltipContentForDate
                          ? tooltipContentForDate({ ...dateObj })
                          : null
                      }
                      onClick={() =>
                        onDateSelected(dateObj, updateActiveMonths)
                      }
                      variant={currentVariant}
                      onMouseEnter={() => onDateHover?.(dateObj.date)}
                    >
                      {dateObj.date.getDate()}
                    </Cell>
                  );
                }),
              ),
            [
              hasEventForDate,
              onDateSelected,
              tooltipContentForDate,
              updateActiveMonths,
              weeks,
              onDateHover,
              endDate,
              month,
              range,
              startDate,
              stateHoveredDate,
              year,
              shouldShowSelection,
            ],
          )}
        </div>
      </div>
    );
  },
);

Calendar.displayName = 'Calendar';

const monthPropType = PropTypes.any;

Calendar.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Current calendar data */
  calendar: PropTypes.shape({
    index: PropTypes.number.isRequired,
    firstDayOfMonth: PropTypes.instanceOf(Date).isRequired,
    lastDayOfMonth: PropTypes.instanceOf(Date).isRequired,
    month: PropTypes.number.isRequired,
    year: PropTypes.number.isRequired,
    weeks: PropTypes.arrayOf(
      PropTypes.arrayOf(PropTypes.any.isRequired).isRequired,
    ).isRequired,
    months: PropTypes.arrayOf(monthPropType.isRequired).isRequired,
    weekdays: PropTypes.arrayOf(PropTypes.any).isRequired,
    years: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.number.isRequired,
        label: PropTypes.number.isRequired,
      }).isRequired,
    ).isRequired,
    selectedMonth: optionalPropType(monthPropType),
    selectedYear: optionalPropType(
      PropTypes.shape({
        key: PropTypes.number.isRequired,
        label: PropTypes.number.isRequired,
      }),
    ),
  }).isRequired,
  /** all of the calendars */
  calendars: PropTypes.array.isRequired,
  /** on Date hover callback */
  onDateHover: PropTypes.func,
  /** current date hovered */
  stateHoveredDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates after minDate, overrides `disablePastDates` */
  minDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates before maxDate, overrides `disableFutureDates` */
  maxDate: PropTypes.instanceOf(Date),
  /** Flag whether the date picker is for a date range of a single value */
  range: PropTypes.bool,
  onDateSelected: PropTypes.func.isRequired,
  updateActiveMonths: PropTypes.func.isRequired,
  hasEventForDate: PropTypes.func,
  tooltipContentForDate: PropTypes.func,
  prevMonth: PropTypes.func.isRequired,
  nextMonth: PropTypes.func.isRequired,
  setMonth: PropTypes.func.isRequired,
  setYear: PropTypes.func.isRequired,
  /** A selected start date for a calendar with range data (should be used with range=true) */
  startDate: PropTypes.instanceOf(Date),
  /** A selected end date for a calendar with range data (should be used with range=true) */
  endDate: PropTypes.instanceOf(Date),
};

export default Calendar;
